import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ActiveCartFacade, Cart} from '@spartacus/cart/base/root';
import {CmsService, TranslationService, User} from '@spartacus/core';
import {B2BUnit} from '@spartacus/core/src/model/org-unit.model';
import {BehaviorSubject, combineLatest, Observable, Subject, Subscription} from 'rxjs';
import {debounceTime, first, take, tap} from 'rxjs/operators';
import {B2bUnitService} from '@app/services/b2b-unit.service';
import {ConfigService, FeatureTogglesService, JsonService} from '@dcp/configurator';
import {B2bUnitRequest} from '@app/model/b2b-unit-request';
import {
  B2bCustomerTaxDataRequest,
  CountryBpTaxConfig,
  CustomerTaxInformation,
  TaxIdValidationModel,
  TaxIdValidationPayload
} from '@app/model/dcp-config-service/tax-config';
import {AddressUsage} from '@app/model/order/dcp-order-entry.model';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {DcpConfigService} from '@app/services/dcp-config.service';
import {DcpCustomerRegistrationDecoratorService} from '@app/services/dcp-customer-registration-decorator.service';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {VALIDATION_RULES} from './common/constants';
import {UploadAttachmentsService} from './services/upload-attachments.service';
import {TaxInformationValidationService} from './services/validation.service';
import {DefaultModalService} from '@app/shared/modal/services/default-modal.service';
import {
  CmsFlexComponentData
} from '@app/checkout/order-confirmation/order-confirmation-list-item/order-confirmation-order-item.model';
import UDExTooltip from '@udex/web-components/dist/Tooltip';
import UDExButton from '@udex/web-components/dist/Button';
import UDExTextField from '@udex/web-components/dist/TextField';

export const TAX_INFORMATION_COMPONENT_CMS_ID = 'TaxInformationComponent';
const TAX_ID = 'taxId';

@UntilDestroy()
@Component({
  selector: 'dcp-tax-information',
  templateUrl: './tax-information.component.html',
  styleUrls: ['./tax-information.component.scss']
})
export class TaxInformationComponent implements OnInit, OnDestroy, AfterViewChecked {

  @Input() b2bUnit$: Observable<B2BUnit>;
  @Input() taxValidationSubject: BehaviorSubject<any> = new BehaviorSubject<any>({});
  @ViewChild('UDExTaxIdTooltip')
  UDExTaxIdTooltip: ElementRef<UDExTooltip>;
  @ViewChild('UDExTaxIdTooltipOpener')
  UDExTaxIdTooltipOpener: ElementRef<UDExButton>;
  @ViewChild('upload')
  fileInput: ElementRef;

  taxForm: FormGroup;
  userFromTaxExemptionCountry: boolean = false;
  exemptionCountryIsoCode: string;
  shouldShowTaxIdValidationError = false;
  isTaxExemptionCheckboxChecked: boolean;
  hasTaxAttachments: boolean = false;
  taxIdRequiredForBilling: boolean = false;
  taxTypePlaceholderDescription: string;
  taxTypeTooltipDescription: string;
  showTaxIdFieldToggle: boolean = false;
  showWarningMessage: boolean = false;
  taxIdValidationFields: TaxIdValidationModel = {};
  shouldUpdateTaxId: boolean = true;
  user: User;
  regexNumberPattern: string = '^-?[0-9]\\d*(\\.\\d{1,2})?$';
  getUserSubscription: Subscription = Subscription.EMPTY;
  removeFileFromCartSubscription: Subscription = Subscription.EMPTY;
  activeCartSubscription: Subscription = Subscription.EMPTY;
  taxIdsSubscription: Subscription = Subscription.EMPTY;
  componentDataSubscription: Subscription = Subscription.EMPTY;
  taxInformationComponentTextData: string;
  private taxInfoInputSubject = new Subject<Event>();

  @ViewChild('modalContentTemplate') modalContentTemplate: TemplateRef<any>;
  modalTitleKey = 'checkout.digitalNativeCheckout.tax.modal.popUpHeader';
  @ViewChild('taxEntryField')
  taxEntryField: ElementRef<UDExTextField>

  constructor(
    public service: UploadAttachmentsService,
    public changeDetectorRef: ChangeDetectorRef,
    public activeCartFacade: ActiveCartFacade,
    public configService: ConfigService,
    public countrySelectorService: CountrySelectorService,
    private b2bUnitService: B2bUnitService,
    private dcpConfigService: DcpConfigService,
    private registrationService: DcpCustomerRegistrationDecoratorService,
    private togglesService: FeatureTogglesService,
    private jsonService: JsonService,
    private fromBuilder: FormBuilder,
    private validationService: TaxInformationValidationService,
    protected modalService: DefaultModalService,
    private cmsService: CmsService,
    private translationService: TranslationService,
  ) {
  }

  ngOnInit() {
    this.subscribeToToggle();
    this.subscribeToTaxValidation();
    this.createTaxFormGroup();
    this.subscribeToUser();
    this.initTaxExemptionFeature();
    this.init();
    this.mapTaxInformationPopupComponentData();
    this.removeFileFromCartSubscription = this.service.langUpdated.pipe(untilDestroyed(this)).subscribe(value => {
      this.service.isValidationErrorPresent = value;
      this.changeDetectorRef.detectChanges();
    });
    this.activeCartSubscription = this.activeCartFacade.getActive().pipe(untilDestroyed(this)).subscribe((cart: Cart) => {
      this.appendExistingTaxExemptionCertificate(cart);
    });
    this.service.hasTaxAttachments().pipe(untilDestroyed(this)).subscribe(hasTaxDoc => {
      this.hasTaxAttachments = !hasTaxDoc;
      this.changeDetectorRef.detectChanges();
    });
    this.service.getWarningMessageSubject().pipe(untilDestroyed(this)).subscribe(showMessage => {
      this.showWarningMessage = showMessage;
      this.changeDetectorRef.detectChanges();
    });
    this.registrationService.updateTaxIdSubject.pipe(untilDestroyed(this)).subscribe(taxId => {
      if (taxId) {
        this.taxForm.get(TAX_ID).patchValue(taxId);
        this.shouldUpdateTaxId = false;
        document.getElementById(taxId).setAttribute('value', taxId)
      }
    });
    this.translationService.translate(this.modalTitleKey).pipe(untilDestroyed(this)).subscribe((translation) => {
      this.modalTitleKey = translation
    })
    this.subscribeToTaxInfoInputSubject();
  }

  ngAfterViewChecked() {
    if (this.taxEntryField && this.taxForm.get(TAX_ID).disabled) {
      this.taxEntryField.nativeElement.setAttribute('readonly', 'true')
    }
  }

  subscribeToToggle() {
    this.togglesService.isEnabled(UNLEASH_TOGGLES.showTaxIdField).pipe(untilDestroyed(this)
    ).subscribe((isEnabled) => {
      this.showTaxIdFieldToggle = isEnabled;
      if (this.taxForm) {
        if (!isEnabled) {
          this.taxForm.get(TAX_ID).setValidators([Validators.maxLength(15), Validators.pattern(this.regexNumberPattern)]);
        }
      }
    });
  };

  subscribeToUser() {
    combineLatest([this.b2bUnit$, this.registrationService.getCurrentUserWithJson()]).pipe(take(1), untilDestroyed(this))
      .subscribe(([b2bUnit, user]) => {
        this.user = user;
        if (this.showTaxIdFieldToggle) {
          this.taxIdsSubscription = this.dcpConfigService
            .getRequiredTaxIds(this.validationService.mapCountryBpTaxPayload(user))
            .pipe(untilDestroyed(this))
            .subscribe((bpTaxConfig: CountryBpTaxConfig) => {
              this.taxIdRequiredForBilling = bpTaxConfig.taxIdRequiredForBilling;
              this.taxIdValidationFields.taxType = this.taxIdValidationFields.taxType || bpTaxConfig.taxType;
              this.fillTaxIdLabelDescriptions(bpTaxConfig);
              this.sendTaxValidationPayload();
              this.changeDetectorRef.detectChanges();
            });
        }
        this.initTaxInfo(b2bUnit);
        this.subscribeToCheckoutSubject();
      });
  }

  subscribeToTaxValidation() {
    this.taxValidationSubject.pipe(untilDestroyed(this)).subscribe(taxValidationResult => {
      if (taxValidationResult) {
        if (taxValidationResult.isValidationError) {
          this.fillTaxIdValidationErrorData(taxValidationResult.errorObject);
        }
        if (taxValidationResult.successCall) {
          this.shouldShowTaxIdValidationError = false;
        }
        this.taxIdValidationFields.isSummaryValidationPerformed = taxValidationResult.isSummaryValidationPerformed;
      }
    });
  }

  /**
   * Subscribing to updating B2BCustomer.json only when customer is retrieved
   */
  subscribeToCheckoutSubject() {
    this.service.getContinueToCheckoutSubject().pipe(untilDestroyed(this)).subscribe(checkoutSubject => {
      if (this.isCustomerTaxValid(checkoutSubject?.status)) {
        this.sendB2bCustomerTax(checkoutSubject);
      }
      if (this.isTaxFieldsValid(checkoutSubject?.status)) {
        this.sendOrgUnitCustomer();
      }
    });
  }

  /**
   * Updating tax validation payload if FT is on and there's no already existing taxId on b2bUnit
   */
  sendTaxValidationPayload(): void {
    if (this.taxIdRequiredForBilling) {
      this.b2bUnit$.pipe(untilDestroyed(this)).subscribe(b2bUnit => {
        this.taxValidationSubject.next({
          taxIdValidationPayload: !Boolean(b2bUnit.taxID) ? this.getCurrentTaxValidationPayload() : undefined
        });
      });
    }
  }

  isCustomerTaxValid(status: boolean): boolean {
    return status && this.showTaxIdFieldToggle && this.taxIdRequiredForBilling && !this.shouldShowTaxIdValidationError && !this.taxForm.get(TAX_ID).disabled;
  }

  isTaxFieldsValid(status: boolean): boolean {
    return !this.showTaxIdFieldToggle && status && this.isFormControlNotDisabled() && this.isFormValidAndOneInputIsFull();
  }

  isFormControlNotDisabled(): boolean {
    return !this.taxForm.get(TAX_ID).disabled;
  }

  isFormValidAndOneInputIsFull(): boolean {
    return this.taxForm.valid && !!this.taxForm.get(TAX_ID).value;
  }

  /**
   * Initializing data containing tax exemption information: current user country, tax exemption countries
   */
  initTaxExemptionFeature() {
    const taxExemptionCountryIsoCodes: string[] = this.configService.getStringArray('checkout.taxExemptionCountriesIsoCodes', []);
    this.userFromTaxExemptionCountry = taxExemptionCountryIsoCodes.includes(this.countrySelectorService.getCurrentCountry().isocode);
    if (this.userFromTaxExemptionCountry) {
      this.exemptionCountryIsoCode = this.countrySelectorService.getCurrentCountry().isocode;
    }
  }

  /**
   * set Tax ID field label, placeholder and tooltip content if it is retrieved from tax service.
   *
   * @param bpTaxConfig
   */
  fillTaxIdLabelDescriptions(bpTaxConfig: CountryBpTaxConfig) {
    this.taxTypePlaceholderDescription = bpTaxConfig?.taxTypeDescription ? bpTaxConfig.taxTypeDescription.substring(bpTaxConfig.taxTypeDescription.indexOf(':') + 1).trim() : '';
    if (this.taxTypePlaceholderDescription.trim().endsWith('*')) {
      const commaSeparatorIndex: number = this.taxTypePlaceholderDescription.lastIndexOf(',');
      if (commaSeparatorIndex >= 0) {
        this.taxTypePlaceholderDescription = this.taxTypePlaceholderDescription.substring(0, commaSeparatorIndex);
      } else {
        this.taxTypePlaceholderDescription = this.taxTypePlaceholderDescription.substring(0, this.taxTypePlaceholderDescription.lastIndexOf('.'));
      }
    }
    this.taxTypeTooltipDescription = bpTaxConfig?.taxIdFormatDescription;
  }

  ngOnDestroy() {
    this.removeFileFromCartSubscription.unsubscribe();
    this.activeCartSubscription.unsubscribe();
    this.getUserSubscription.unsubscribe();
    this.taxIdsSubscription.unsubscribe();
  }

  public async init() {
    if (this.service.form) {
      return;
    }
    await this.service.initForm(VALIDATION_RULES);
  }

  /**
   * Defining whether to show file uploading widget based on whether corresponding checkbox
   * is checked or not.
   *
   * @param checkboxCheckedStatus determines whether changed to 'checked' or not.
   */
  onTaxExemptionCheckboxChange(checkboxCheckedStatus: any): void {
    checkboxCheckedStatus.preventDefault();
    if (this.isTaxExemptionCheckboxChecked && this.service.Attachments && this.service.Attachments.length > 0) {
      this.service.removeUploadedFile(0);
    }
    this.isTaxExemptionCheckboxChecked = !this.isTaxExemptionCheckboxChecked;
    this.changeDetectorRef.detectChanges();
    this.service.isTaxExemptionCheckboxCheckedSubject.next(this.isTaxExemptionCheckboxChecked);
    if (!this.isTaxExemptionCheckboxChecked) {
      this.service.showWarningMessageSubject.next(false);
    }
  }

  openExemptionCertificateDetailsModal() {
    this.modalService.openModal(this.modalContentTemplate, this.modalTitleKey);
  }

  /**
   * If there is an attached tax exemption certificate exists for the currently active cart,
   * then automatically checking the checkbox that user has tax exemption cert., and
   * appending it as already uploaded file.
   *
   * @param cart - currently active cart
   * @private - because this is tax exemption certificate specific method
   */
  private appendExistingTaxExemptionCertificate(cart: Cart): void {
    if (cart.attachedDocuments) {
      if (this.service.Attachments.length > 0) {
        this.isTaxExemptionCheckboxChecked = true;
        this.service.showWarningMessageSubject.next(false);
        return;
      }
      cart.attachedDocuments.forEach(fetchedFile => {
        if (fetchedFile.code?.endsWith('taxCertificate')) {
          this.service.savedFileCode = fetchedFile.code;
          this.isTaxExemptionCheckboxChecked = true;
          const uploadedFileMirror = new File([], fetchedFile.url.substring(8, fetchedFile.url.indexOf('?context=')));
          this.service.addAttachment(uploadedFileMirror, VALIDATION_RULES, false);
        }
      });
    }
  }

  onRemoveDocument(i: number) {
    this.service.isTaxExemptionCheckboxCheckedSubject.next(this.isTaxExemptionCheckboxChecked);
    this.service.showWarningMessageSubject.next(false);
    this.service.removeUploadedFile(i);
  }

  /**
   * Initializing tax information when user is available
   *
   * @param b2bUnit
   */
  initTaxInfo(b2bUnit): void {
    const soldToAddress = b2bUnit.addresses?.find(address => AddressUsage.SOLD_TO === address.addressUsage?.trim().toUpperCase());
    if (this.shouldUpdateTaxId) {
      if (soldToAddress?.taxID) {
        this.taxForm.get(TAX_ID).patchValue(soldToAddress.taxID);
        this.taxIdValidationFields.taxType = soldToAddress.taxType || this.taxIdValidationFields.taxType;
      } else {
        this.taxForm.get(TAX_ID).patchValue(b2bUnit.taxID);
      }
    }
    this.setTaxIdValidationData(b2bUnit, soldToAddress);
    this.checkDisable(TAX_ID, Boolean(b2bUnit.taxID || soldToAddress?.taxID));
    this.changeDetectorRef.detectChanges();
    this.sendTaxValidationPayloadFilled(b2bUnit);
  }

  /**
   * In case tax id toggle is ON, updating validation payload if tax is required for billing,
   * and also updating it every time when taxId is changed by user input.
   *
   * @param b2bUnit
   */
  sendTaxValidationPayloadFilled(b2bUnit: B2BUnit) {
    if (this.showTaxIdFieldToggle) {
      this.taxForm.get(TAX_ID).valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
        this.taxValidationSubject.next({
          taxIdValidationPayload: this.getCurrentTaxValidationPayload()
        });
      });
      if (this.taxIdRequiredForBilling) {
        this.taxValidationSubject.next({
          taxIdValidationPayload: Boolean(b2bUnit.taxID) ? undefined : this.getCurrentTaxValidationPayload()
        });
      }
    }
  }

  /**
   * Prefill tax id field with b2b unit > user json[taxData] info if present
   *
   * @param b2bUnit
   * @param soldToAddress
   */
  setTaxIdValidationData(b2bUnit: B2BUnit, soldToAddress) {
    if (!b2bUnit.taxID && !soldToAddress?.taxID && this.user.json && this.showTaxIdFieldToggle) {
      const taxDataJsonValue = this.user.json.find(json => json.type === 'taxData')?.value;
      if (taxDataJsonValue) {
        const customerTaxID: CustomerTaxInformation = this.jsonService.parseSafely(taxDataJsonValue);
        if (customerTaxID && this.shouldUpdateTaxId) {
          this.taxForm.get(TAX_ID).patchValue(customerTaxID.taxId);
        }
      }
    }
  }

  sendOrgUnitCustomer() {
    const request: B2bUnitRequest = {
      b2bUnitUid: this.user.orgUnit.uid,
      b2bCustomerUid: this.user.uid,
      customerId: this.user.customerId,
      taxId: this.taxForm.get(TAX_ID).value,
      taxType: this.taxIdValidationFields.taxType
    };
    this.b2bUnitService.sendTaxInfo({body: request}).pipe(first(), untilDestroyed(this))
      .subscribe(() => this.service.continueToCheckoutSubject.next({
        status: false,
        userId: undefined,
        customerId: undefined
      }));
  }

  sendB2bCustomerTax(checkoutSubject) {
    const request: B2bCustomerTaxDataRequest = {
      scimUserId: this.user.scimUserId,
      taxType: this.taxIdValidationFields.taxType,
      customerId: checkoutSubject?.customerId || this.user.customerId,
      taxId: this.taxForm.get(TAX_ID).value
    };
    this.service.updateB2bCustomerTax(request);
  }

  createTaxFormGroup() {
    this.taxForm = this.fromBuilder.group({
      taxId: new FormControl({value: '', disabled: true}, !this.showTaxIdFieldToggle ?
        [Validators.maxLength(15), Validators.pattern(this.regexNumberPattern)] : [])
    });
  }

  checkDisable(control: string, status: boolean) {
    if (status) {
      this.taxForm.controls[control].disable();
    } else {
      this.taxForm.controls[control].enable();
    }
  }

  /**
   * Validating taxId on change
   *
   */
  updateTaxInfo(event: any): void {
    this.taxInfoInputSubject.next(event);
    // web components don't support formControl-API, so we need to patch value manually
    this.taxForm.get(TAX_ID).patchValue(event.srcElement.value);
  }

  /**
   * React on (delayed) change to taxInfoInputSubject
   * Relay necessary to avoid too many requests. We react on input event which is fired after every keystroke.
   * Therefore, adding a debounce time to wait after each keystroke before sending the request.
   */
  subscribeToTaxInfoInputSubject(): void {
    this.taxInfoInputSubject.pipe(
      debounceTime(1000),
      tap(event => {
        if (this.showTaxIdFieldToggle && !this.taxIdValidationFields.isSummaryValidationPerformed) {
          this.taxValidationSubject.next({
            isValidationStarted: true,
            taxIdValidationPayload: this.getCurrentTaxValidationPayload()
          });
          this.dcpConfigService.validateTaxIds(this.getCurrentTaxValidationPayload())
            .pipe(
              untilDestroyed(this),
            )
            .subscribe(() => {
              this.taxValidationSubject.next({
                isValidationStarted: false,
                successCall: true
              });
            }, errorResponse => {
              this.fillTaxIdValidationErrorData(errorResponse);
              this.taxValidationSubject.next({
                isValidationStarted: false
              });
            });
        }
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  /**
   * Setting tax id validation error response fields.
   *
   * @param errorResponse
   */
  fillTaxIdValidationErrorData(errorResponse) {
    // 500 excluded b.c. it's thrown in a wrong encoding case what's still a user input issue.
    this.taxIdValidationFields.isTaxServerError = errorResponse.status > 500;

    this.taxForm.get(TAX_ID).setErrors({taxIdValue: true});
    this.shouldShowTaxIdValidationError = true;
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Getting current value of tax-id-validation related fields as a payload.
   */
  getCurrentTaxValidationPayload(): TaxIdValidationPayload {
    return this.validationService.getCurrentTaxValidationPayload(
      this.taxIdValidationFields.taxType,
      this.taxForm?.get(TAX_ID).value,
      this.user);
  }

  mapTaxInformationPopupComponentData(): void {
    this.componentDataSubscription = this.cmsService.getComponentData(TAX_INFORMATION_COMPONENT_CMS_ID + this.exemptionCountryIsoCode)
      .pipe(untilDestroyed(this))
      .subscribe((data: CmsFlexComponentData) => {
        this.taxInformationComponentTextData = data?.classificationsProductData;
      });
  }

  public onFileChange($event: Event): void {
    const fileInput = $event.target as HTMLInputElement;
    this.service.addAttachment(fileInput.files[0], VALIDATION_RULES, true);
    // clear the input
    this.fileInput.nativeElement.value = '';
  }
}
